import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import HeroPromo from "../components/HeroPromo";
import { PremiereBillboard } from "../components/PremiereBillboard";
import { ArrowRight, Zap, TrendingUp, Shield, Sparkles, DollarSign, Clock, Gauge, Star } from "lucide-react";

export default function Landing({ onOpenConcierge }) {
  const nav = useNavigate();
  const [city, setCity] = useState("");

  const search = (e) => {
    e?.preventDefault();
    nav(`/browse${city ? `?city=${encodeURIComponent(city)}` : ""}`);
  };

  return (
    <main className="relative" data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden min-h-[88vh] flex items-center">
        <div className="absolute inset-0 -z-10">
          <img src="https://images.unsplash.com/photo-1773104398730-466691f5ecca?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
               alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0D12] via-[#0B0D12]/90 to-[#0B0D12]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D12] via-transparent to-transparent" />
        </div>

        <div className="w-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-10 md:pt-14 rise">
          <p className="text-[11px] font-mono uppercase tracking-[0.35em] text-blue-300 mb-3" data-testid="landing-premiere-kicker">Velocity Films · Christmas Day premiere</p>
          <PremiereBillboard large />
        </div>
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-20 grid-cols-1 lg:grid-cols-2 grid gap-12 items-center">
          <div className="rise">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-800 bg-slate-900/60 backdrop-blur mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 live-dot" />
              <span className="text-xs font-mono tracking-widest uppercase text-slate-300">For hosts · For gig drivers · 2026</span>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[0.95] mb-6">
              Your car's<br />
              <span className="text-kinetic">second shift</span><br />
              earns the rent.
            </h1>

            <p className="text-base sm:text-lg text-slate-300 max-w-xl mb-8 rise rise-1">
              MY VELOCITY is the next-gen marketplace built for gig drivers &amp; hosts. List your car, match with rideshare-ready renters, and earn with AI-tuned pricing — more than Turo lets you keep.
            </p>

            <form onSubmit={search} className="rise rise-2 flex flex-col sm:flex-row gap-3 max-w-xl mb-10">
              <Input value={city} onChange={(e) => setCity(e.target.value)}
                placeholder="City (e.g., Los Angeles, Miami)"
                data-testid="search-city-input"
                className="h-14 bg-slate-900/70 backdrop-blur border-slate-700 text-base" />
              <Button type="submit" data-testid="hero-search-btn" size="lg"
                className="h-14 px-8 bg-white text-slate-950 hover:bg-slate-100 font-semibold btn-glow">
                Find a car <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </form>

            <div className="rise rise-3 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-400">
              <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-blue-400" /> Verified owners</span>
              <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-cyan-400" /> Gig-ready filters</span>
              <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-emerald-400" /> AI pricing</span>
            </div>
          </div>

          <div className="rise rise-2 space-y-5">
            <HeroPromo />
            <div className="relative hidden lg:block">
              <div className="glass rounded-3xl p-6 relative overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs font-mono uppercase tracking-widest text-slate-400">Live earnings</p>
                    <p className="font-display text-4xl font-extrabold text-emerald-400 mt-1">$8,247.00</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-mono uppercase tracking-widest text-slate-400">This month</p>
                    <p className="text-sm text-emerald-400 flex items-center gap-1 justify-end mt-1"><TrendingUp className="w-3.5 h-3.5" /> +23%</p>
                  </div>
                </div>
                <div className="h-32 relative">
                  <svg viewBox="0 0 300 100" className="w-full h-full">
                    <defs>
                      <linearGradient id="grad" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#10B981" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d="M0 80 L30 70 L60 72 L90 55 L120 60 L150 42 L180 48 L210 30 L240 36 L270 20 L300 15 L300 100 L0 100 Z" fill="url(#grad)" />
                    <path d="M0 80 L30 70 L60 72 L90 55 L120 60 L150 42 L180 48 L210 30 L240 36 L270 20 L300 15" stroke="#10B981" strokeWidth="2" fill="none" />
                  </svg>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-800">
                  <div><p className="text-xs text-slate-500 font-mono uppercase">Trips</p><p className="font-display font-bold text-lg">47</p></div>
                  <div><p className="text-xs text-slate-500 font-mono uppercase">Hours</p><p className="font-display font-bold text-lg">312</p></div>
                  <div><p className="text-xs text-slate-500 font-mono uppercase">Rating</p><p className="font-display font-bold text-lg flex items-center gap-1">4.9 <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" /></p></div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-blue-500 rounded-xl px-3 py-2 rotate-3 shadow-xl shadow-blue-500/40">
                <p className="text-xs font-mono uppercase tracking-wider text-slate-950">AI priced</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="py-20 md:py-28 relative" data-testid="value-props">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="font-mono text-xs tracking-widest uppercase text-cyan-400 mb-3">// Why MY VELOCITY</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">Built for the gig economy, not the hotel lobby.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon: DollarSign, title: "80% to hosts", body: "We take a lean 10% service fee. The rest is yours. No surprise cleaning deductions." },
              { icon: Sparkles, title: "AI Smart Pricing", body: "Claude-powered pricing suggestions by make, year, city & gig demand. Set & forget." },
              { icon: Zap, title: "Gig-Ready Match", body: "Filter cars that fit Uber/Lyft/Doordash requirements. Fast approvals for drivers." },
              { icon: Clock, title: "Instant Booking", body: "Pay in Stripe, unlock the trip. Pickup codes delivered the second payment clears." },
              { icon: Gauge, title: "Earnings Dashboard", body: "Real-time payouts, trip history, and performance charts for every car in your fleet." },
              { icon: Shield, title: "Verified Fleet", body: "KYC checks, driving history validation, and insurance scaffolding ready to plug in." },
            ].map((f, i) => (
              <div key={i} className="group p-6 rounded-2xl bg-[#131722]/80 border border-slate-800 hover:border-blue-500/40 transition-all duration-500 rise" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="w-11 h-11 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center mb-4 group-hover:border-blue-500/60 group-hover:text-blue-400 transition-colors">
                  <f.icon className="w-5 h-5 text-cyan-400" />
                </div>
                <h3 className="font-display text-xl font-bold mb-2">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="relative rounded-3xl overflow-hidden">
            <img src="https://images.unsplash.com/photo-1785911177775-ddac53ab1c63?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
                 alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/80 to-blue-950/40" />
            <div className="relative p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
              <div>
                <p className="font-mono text-xs tracking-widest uppercase text-cyan-400 mb-3">// Start today</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight max-w-2xl">List your car. Earn by Friday.</h2>
                <p className="text-slate-300 mt-3 max-w-xl">AI drafts the listing. We handle payment. You keep the keys when it's not booked.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => nav("/list-your-car")} data-testid="cta-list-btn" size="lg" className="bg-white text-slate-950 hover:bg-slate-100 font-semibold btn-glow">
                  List a car <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button onClick={onOpenConcierge} data-testid="cta-concierge-btn" variant="outline" size="lg" className="bg-transparent border-slate-700 hover:bg-slate-800">
                  <Sparkles className="w-4 h-4 mr-2 text-cyan-400" /> Ask the AI
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-800/60 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <p>© 2026 MY VELOCITY. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/browse" className="hover:text-slate-300">Browse</Link>
            <Link to="/list-your-car" className="hover:text-slate-300">List a car</Link>
            <Link to="/gig-hub" className="hover:text-slate-300">Gig Hub</Link>
          </div>
        </div>
      </footer>
    </main>
  );
}
